import React, { useRef, useCallback } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import Text from 'common/components/Text'
import Icon from 'common/components/Icon'
import BoardTitleContainer from './BoardTitleContainer'
import Button from 'common/components/Button'
import getUrl from '../../../utils/getUrl'
import Label, { LabelSet } from 'common/components/Label'
import { useTranslation } from 'react-i18next'
import useToggleMenu from '../../../hooks/useToggleMenu'
import usePopper from 'common/hooks/usePopper'
import BoardOptionsMenu from '../../BoardOptionsMenu'

const collapse = 500 //  below this, collapse the folder names into tooltips

export const BoardTitle = ({
  board,
  block,
  anon,
  minimal,
  goToFolder,
  folders,
  pendingUploads,
  boardWidth
}) => {
  const small = boardWidth < collapse

  const menuRef = useRef()
  const [menuOpen, showMenu, hideMenu] = useToggleMenu(menuRef)
  const showMenuOnly = useCallback(
    e => {
      e.stopPropagation()
      showMenu()
    },
    [showMenu]
  )

  const menuButtonRef = useRef()
  const { style: menuStyle } = usePopper(
    {
      referenceRef: menuButtonRef,
      popperRef: menuRef,
      modifiers: {
        offset: { offset: '0, 5' }
      }
    },
    [menuOpen]
  )

  const { t } = useTranslation()
  return (
    <LabelSet>
      {/* Folders */}
      {!minimal &&
        folders.map(folder => (
          <Button
            onClick={() => goToFolder(folder)}
            key={folder.id}
            tooltip={small ? folder.name : t('toolbar.tooltips.folder')}
          >
            <Icon icon="folder" color={folder.color} />
            {!small && <Text>{folder.name}</Text>}
          </Button>
        ))}

      {/* Board */}
      <Button to={block && getUrl(board)}>
        {pendingUploads ? (
          <Icon icon="loader" style={{ borderColor: board.color }} />
        ) : (
          <Icon icon="board" color={board.color} active />
        )}
        {!minimal && (
          <Text medium truncate>
            {board.name}
          </Text>
        )}
      </Button>

      {/* Block */}
      {!minimal && block && (
        <Label>
          <Text black medium truncate>
            {block.name}
          </Text>
        </Label>
      )}

      {!anon && !minimal && !block && (
        <Button
          onClick={showMenuOnly}
          active={menuOpen}
          ref={menuButtonRef}
          tooltip={t('toolbar.tooltips.boardOptions')}
        >
          <Icon icon="ellipsis" />
        </Button>
      )}

      {menuOpen &&
        ReactDOM.createPortal(
          <BoardOptionsMenu
            itemId={board.id}
            edit={() => {}}
            hide={hideMenu}
            innerRef={menuRef}
            style={menuStyle}
          />,
          document.body
        )}
    </LabelSet>
  )
}

BoardTitle.propTypes = {
  board: PropTypes.object.isRequired,
  pendingUploads: PropTypes.bool.isRequired,
  to: PropTypes.string
}

export default BoardTitleContainer(BoardTitle)
